import React from 'react';
import { ShieldCheck, GitBranch, Users, FileLock2, History, ScrollText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GOVERNANCE_COPY } from '@/lib/governanceCopy';

export interface GovernanceActiveBlockProps {
  /** Visual density. `compact` removes the title and reduces spacing. */
  variant?: 'default' | 'compact';
  className?: string;
}

/**
 * GovernanceActiveBlock
 *
 * Bloco institucional estático e transversal — apresenta de forma resumida os
 * princípios operacionais já existentes da plataforma (segregação de funções,
 * versionamento contratual, origem auditável, consolidação de registros e
 * metodologia uniforme).
 *
 * Regras:
 *  - 100% estático: zero queries, zero chamadas de rede, zero leitura de estado.
 *  - Linguagem de negócio: não expõe nomes técnicos, tabelas, RPCs, eventos,
 *    hashes ou estruturas internas.
 *  - Não altera RBAC, RLS, regras de negócio ou fluxos operacionais.
 *  - Reapresenta capacidades já implementadas — não adiciona funcionalidade.
 *
 * Rollback: remover este componente das páginas e excluir o arquivo.
 */
export const GovernanceActiveBlock: React.FC<GovernanceActiveBlockProps> = ({
  variant = 'default',
  className,
}) => {
  const items = GOVERNANCE_COPY.governancePrinciples;
  const isCompact = variant === 'compact';

  const ICONS = [ShieldCheck, GitBranch, Users, FileLock2, History, ScrollText];

  return (
    <section
      aria-label="Governança ativa do sistema"
      className={cn(
        'rounded-[10px] border border-border/50 bg-muted/20',
        isCompact ? 'p-3' : 'p-4 sm:p-5',
        className,
      )}
    >
      {!isCompact && (
        <header className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.8} />
          <h3 className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
            {GOVERNANCE_COPY.governanceBlock.title}
          </h3>
        </header>
      )}

      <ul
        className={cn(
          'grid gap-x-4 gap-y-2',
          'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {items.map((it, i) => {
          const Icon = ICONS[i % ICONS.length];
          return (
            <li key={it.label} className="flex items-start gap-2">
              <Icon
                className="h-3.5 w-3.5 mt-0.5 text-muted-foreground/80 flex-shrink-0"
                strokeWidth={1.8}
              />
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground/85 leading-tight">
                  {it.label}
                </p>
                <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                  {it.description}
                </p>
              </div>
            </li>
          );
        })}
      </ul>

      {!isCompact && (
        <p className="text-[10.5px] text-muted-foreground/70 italic mt-3 leading-snug">
          {GOVERNANCE_COPY.governanceBlock.footnote}
        </p>
      )}
    </section>
  );
};

export default GovernanceActiveBlock;
